import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineListUlIcon from 'LuxKit/Icons/line/LineListUlIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import RightPanelEmptyState from '../components/RightPanelEmptyState'
import RightPanelLoadingState from '../components/RightPanelLoadingState'
import { MARKER_TYPE_STOP } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  RightPanel,
  RightPanelLayoutMeasure,
  RightPanelHeader,
  RightPanelHeaderLeadingControl,
  RightPanelContent,
  EmptyStateWrapper,
} from '../styles/layoutStyles'
import { HeaderControlButton } from 'tripPlanner/components/GenericDrawer/TripDrawerHeader'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useExploreMapReturn } from '../hooks/useExploreMapReturn'
import { getTripChatCheckoutReturnView, useRightPanelState } from '../hooks/useRightPanelState'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import MapContainer from './MapContainer'
import ItineraryContainer from './ItineraryContainer'
import ListContainer from './ListContainer'
import CartPanelContainer from './CartPanelContainer'
import TripListMobileView from '../components/ListView/TripListMobileView'
import TripListView from '../components/ListView/TripListView'
import useTripListDesktopLayout from '../components/ListView/useTripListDesktopLayout'
import { useTripListRenderContent } from '../components/ListView/useTripListRenderContent'
import { hasRenderableTimelineContent } from '../components/tripPlanCanvasContent'
import { getRightPanelRenderState } from '../utils/rightPanelRenderState'
import { useTripDrawerPortalTargetRef } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import { RightPanelSnapshotRegistration } from '../contexts/RightPanelSnapshotRegistration'

function RightPanelContainer() {
  const eventBus = useTripPlannerEventBus()
  const intl = useIntl()
  const { sendMessage } = useChatActions()
  const { itineraryData, isItineraryLoading } = useItineraryContext()
  const rightPanelPortalRef = useTripDrawerPortalTargetRef()
  const rightPanelMeasureRef = useRef<HTMLDivElement | null>(null)
  const isDesktopListLayout = useTripListDesktopLayout(rightPanelMeasureRef)
  const ListViewComponent = isDesktopListLayout ? TripListView : TripListMobileView

  const {
    rightPanelView,
    setRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  } = useRightPanelState()

  const { exploreMapReturn, returnToListingSearch } = useExploreMapReturn(rightPanelView)

  const handleTimelineDrawerOpenChange = useCallback((isOpen: boolean) => {
    eventBus.dispatch({ type: 'right_panel_domain_drawer_state_changed', isOpen })
  }, [eventBus])

  // ── Computed state ──

  const hasTripListContent = useTripListRenderContent(itineraryData)

  const hasTimelineContent = useMemo(
    () => hasRenderableTimelineContent(itineraryData),
    [itineraryData],
  )

  const selectedStop = useMemo(() => {
    if (!selectedDayItineraryId || !itineraryData) {
      return undefined
    }
    // Find the stop that contains this day
    for (const stop of itineraryData.stops) {
      const dayExists = stop.days.some((day: { id: number }) => day.id === selectedDayItineraryId)
      if (dayExists) {
        return stop
      }
    }
    return undefined
  }, [selectedDayItineraryId, itineraryData])

  const {
    shouldShowMap,
    shouldShowTimelineLoadingState,
    shouldShowTimelineEmptyState,
    shouldShowTripPlanCanvas,
    shouldShowListLoadingState,
    shouldShowListEmptyState,
    shouldShowListView,
  } = getRightPanelRenderState({
    rightPanelView,
    hasTimelineContent,
    hasTripListContent,
    isItineraryLoading,
  })

  // ── Cart panel animation state (TripDrawer pattern) ──

  const isCartView = rightPanelView === 'cart'
  const [shouldRenderCart, setShouldRenderCart] = useState(isCartView)
  const [isCartClosing, setIsCartClosing] = useState(false)
  const preCartViewRef = useRef<Exclude<RightPanelView, 'cart'>>('map')

  useEffect(() => {
    if (isCartView) {
      setShouldRenderCart(true)
      setIsCartClosing(false)
    } else if (shouldRenderCart) {
      setIsCartClosing(true)
      const timer = setTimeout(() => {
        setShouldRenderCart(false)
        setIsCartClosing(false)
      }, MODAL_TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isCartView, shouldRenderCart])

  const hideToggle = shouldRenderCart

  // ── View toggle ──

  const handleViewToggle = useCallback((view: RightPanelView) => {
    setRightPanelView(view)
  }, [setRightPanelView])

  const handleCartClose = useCallback(() => {
    setRightPanelView(preCartViewRef.current)
  }, [setRightPanelView])

  // ── Event bus subscriptions ──

  useEventSubscription('open_cart_panel', () => {
    setRightPanelView((prev) => {
      if (prev === 'cart') return preCartViewRef.current
      preCartViewRef.current = prev
      return 'cart'
    })
  })

  useEventSubscription('view_itinerary_item', () => {
    setRightPanelView('timeline')
  })

  useEventSubscription('show_offer_suggestion_on_map', (event) => {
    if (event.camera) setSelectedDayItineraryId(undefined)
  })

  useEventSubscription('show_place_on_map', (event) => {
    if (event.camera) setSelectedDayItineraryId(undefined)
  })

  useEventSubscription('reset_chat_state', () => {
    setRightPanelView(getTripChatCheckoutReturnView() ?? 'map')
    setSelectedDayItineraryId(undefined)
    handleMarkerOverlayClose()
  })

  // ── i18n ──

  const timelineOptionTitle = intl.formatMessage({
    id: 'CrXe4aeBNf4hdDEigM3u',
    defaultMessage: 'Timeline',
    description: 'Segmented option title for the timeline view in the trip planner right panel',
  })

  const listOptionTitle = intl.formatMessage({
    id: 'mgKCGdrsVCcLCxmhhoN-',
    defaultMessage: 'List',
    description: 'Segmented option title for the list view in the trip planner right panel',
  })

  const mapOptionTitle = intl.formatMessage({
    id: '3uJcGV6ZPs3TPDucD-0n',
    defaultMessage: 'Map',
    description: 'Segmented option title for the map view in the trip planner right panel',
  })

  const exploreMapBackLabel = intl.formatMessage({
    id: 'lEp2mV6-VQVERALq8DSM',
    defaultMessage: 'Back to things to do',
    description: 'Accessible label for the control on the trip planner map that reopens the things to do drawer it was opened from',
  })

  return (
    <>
      <RightPanelSnapshotRegistration
        rightPanelView={rightPanelView}
        selectedMarkerData={selectedMarkerData}
      />
      <RightPanel ref={rightPanelPortalRef}>
        <RightPanelLayoutMeasure ref={rightPanelMeasureRef}>
          {!hideToggle && (
            <RightPanelHeader className={clsx({ 'has-leading-control': !!exploreMapReturn })}>
              {exploreMapReturn && (
                <RightPanelHeaderLeadingControl>
                  <HeaderControlButton
                    kind="primary"
                    variant="dark"
                    size="medium"
                    shape="circle"
                    onClick={returnToListingSearch}
                    aria-label={exploreMapBackLabel}
                  >
                    <LineArrowLeftIcon />
                  </HeaderControlButton>
                </RightPanelHeaderLeadingControl>
              )}
              <SegmentedOptionsGroup
                inputsName="rightPanelView"
                onValueChange={handleViewToggle}
              >
                <SegmentedOption
                  value="timeline"
                  title={timelineOptionTitle}
                  icon={<LineClockIcon size="XS" />}
                  checked={rightPanelView === 'timeline'}
                />
                <SegmentedOption
                  value="list"
                  title={listOptionTitle}
                  icon={<LineListUlIcon size="XS" />}
                  checked={rightPanelView === 'list'}
                />
                <SegmentedOption
                  value="map"
                  title={mapOptionTitle}
                  icon={<LineMapIcon size="XS" />}
                  checked={rightPanelView === 'map'}
                />
              </SegmentedOptionsGroup>
            </RightPanelHeader>
          )}
          <RightPanelContent>
            <ErrorBoundary fallback={null}>
              <MapContainer
                visible={shouldShowMap}
                selectedDayItineraryId={selectedDayItineraryId}
                selectedStop={selectedStop}
                selectedMarkerData={selectedMarkerData}
                onMarkerSelect={handleMarkerSelect}
                setRightPanelView={setRightPanelView}
                rightPanelView={rightPanelView}
              />
            </ErrorBoundary>
            {shouldShowTimelineLoadingState && (
              <EmptyStateWrapper padding={40}>
                <RightPanelLoadingState />
              </EmptyStateWrapper>
            )}
            {shouldShowTimelineEmptyState && (
              <EmptyStateWrapper padding={40}>
                <RightPanelEmptyState variant="timeline" />
              </EmptyStateWrapper>
            )}
            <ItineraryContainer
              visible={shouldShowTripPlanCanvas}
              selectedDayItineraryId={selectedDayItineraryId}
              selectedStop={selectedStop}
              onDrawerOpenChange={handleTimelineDrawerOpenChange}
            />
            {shouldShowListLoadingState && (
              <EmptyStateWrapper padding={40}>
                <RightPanelLoadingState />
              </EmptyStateWrapper>
            )}
            {shouldShowListEmptyState && (
              <EmptyStateWrapper padding={40}>
                <RightPanelEmptyState variant="list" />
              </EmptyStateWrapper>
            )}
            <ListContainer
              visible={shouldShowListView}
              ViewComponent={ListViewComponent}
            />
          </RightPanelContent>
          {shouldRenderCart && (
            <CartPanelContainer
              isClosing={isCartClosing}
              onClose={handleCartClose}
            />
          )}
          {selectedMarkerData?.marker?.placeId && (
            <PlaceInfoDrawer
              isOpen={Boolean(selectedMarkerData)}
              onClose={handleMarkerOverlayClose}
              googlePlaceId={selectedMarkerData.marker.placeId}
              placeName={selectedMarkerData.marker.title || selectedMarkerData.marker.label || ''}
              itineraryData={itineraryData}
              onSendMessage={sendMessage}
              hideAddToTrip={selectedMarkerData.marker.markerType === MARKER_TYPE_STOP}
            />
          )}
        </RightPanelLayoutMeasure>
      </RightPanel>
    </>
  )
}

export default RightPanelContainer
